import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BulkActionBar from "../../components/BulkActionBar";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
  requestBulkEmsApproval,
} from "../../lib/emsApproval";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/GuidedRegisters.css";
import {
  createNonconformanceRegisterItemApi,
  decideEmsApprovalApi,
  deleteNonconformanceRegisterItemApi,
  downloadNonconformanceRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchNonconformanceRegisterApi,
  requestEmsApprovalApi,
  updateNonconformanceRegisterItemApi,
  verifyEmsClosureApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "non_conformance";

const EMPTY_FORM = {
  iso_clause: "",
  title: "",
  source: "",
  description: "",
  root_cause: "",
  corrective_action: "",
  owner: "",
  due_date: "",
  closure_date: "",
  status: "open",
  evidence: "",
  notes: "",
};

export default function NonConformance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const [isoClauseLabels, setIsoClauseLabels] = useState({});
  const [defaultIsoClause, setDefaultIsoClause] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [verifyRoles, setVerifyRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [closureVerificationRequired, setClosureVerificationRequired] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRequesting, setBulkRequesting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchNonconformanceRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load non-conformance register");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const policyRows = Array.isArray(payload?.policy) ? payload.policy : [];
      const labels = payload?.iso_clause_labels && typeof payload.iso_clause_labels === "object"
        ? payload.iso_clause_labels
        : {};
      const modulePolicy = policyRows.find((r) => String(r?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options)
        ? modulePolicy.iso_clause_options.map((x) => String(x))
        : [];
      const fallbackDefault = String(modulePolicy?.iso_clause_default || options[0] || "");
      const reqRoles = Array.isArray(modulePolicy?.request_roles) ? modulePolicy.request_roles.map((x) => String(x).toLowerCase()) : [];
      const appRoles = Array.isArray(modulePolicy?.approve_roles) ? modulePolicy.approve_roles.map((x) => String(x).toLowerCase()) : [];
      const revRoles = Array.isArray(modulePolicy?.review_roles) ? modulePolicy.review_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const verRoles = Array.isArray(modulePolicy?.verify_roles) ? modulePolicy.verify_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const wfMode = String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase();
      setIsoClauseLabels(labels);
      setDefaultIsoClause(fallbackDefault);
      setRequestRoles(reqRoles);
      setApproveRoles(appRoles);
      setReviewRoles(revRoles);
      setVerifyRoles(verRoles);
      setWorkflowMode(wfMode === "two_step" ? "two_step" : "one_step");
      setClosureVerificationRequired(Boolean(modulePolicy?.closure_verification_required));
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallbackDefault }));
    } catch {
      setIsoClauseLabels({});
      setDefaultIsoClause("10");
      setRequestRoles(["client", "admin"]);
      setApproveRoles(["admin"]);
      setReviewRoles(["admin"]);
      setVerifyRoles(["admin"]);
      setWorkflowMode("one_step");
      setClosureVerificationRequired(false);
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "10" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
    setSubmitAttempted(false);
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!String(form.title || "").trim()) errs.push("Title is required.");
    if (!String(form.description || "").trim()) errs.push("Description is required.");
    if (!String(form.iso_clause || "").trim()) errs.push("ISO clause is required.");
    if (String(form.status || "").trim().toLowerCase() === "closed") {
      if (!String(form.root_cause || "").trim()) errs.push("Root cause is required before setting status to closed.");
      if (!String(form.corrective_action || "").trim()) errs.push("Corrective action is required before setting status to closed.");
      if (!String(form.owner || "").trim()) errs.push("Owner is required before setting status to closed.");
      if (!String(form.due_date || "").trim()) errs.push("Due date is required before setting status to closed.");
      if (!String(form.closure_date || "").trim()) errs.push("Closure date is required before setting status to closed.");
      if (!String(form.evidence || "").trim()) errs.push("Evidence is required before setting status to closed.");
    }
    return errs;
  }, [form]);

  const statusBadgeClass = (status) => `badge badge-status-${String(status || "").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const approvalBadgeClass = (status) => `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const userRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(userRole);
  const canApprove = approveRoles.includes(userRole) && canApproveEmsModule("non_conformance");
  const canReview = reviewRoles.includes(userRole);
  const canVerify = verifyRoles.includes(userRole);
  const completionStatuses = useMemo(() => ["closed"], []);
  const canRequestApprovalForItem = useCallback((item) =>
    canRequestApprovalForRecord(item, canRequest, completionStatuses),
    [canRequest, completionStatuses]
  );
  const isCompletionStatus = (status) => completionStatuses.includes(String(status || "").trim().toLowerCase());

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      title: item.title || "",
      source: item.source || "",
      description: item.description || "",
      root_cause: item.root_cause || "",
      corrective_action: item.corrective_action || "",
      owner: item.owner || "",
      due_date: item.due_date || "",
      closure_date: item.closure_date || "",
      status: item.status || "open",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await updateNonconformanceRegisterItemApi(editingId, form);
        toast.success("Non-conformance updated");
      } else {
        await createNonconformanceRegisterItemApi(form);
        toast.success("Non-conformance created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save non-conformance");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this non-conformance entry?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteNonconformanceRegisterItemApi(id);
      toast.success("Non-conformance deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete non-conformance");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        decision,
        notes,
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision}`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional closure verification note:", "") || "";
      await verifyEmsClosureApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadNonconformanceRegisterCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "nonconformance_register.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Non-conformance register exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export non-conformance register");
    } finally {
      setExporting(false);
    }
  };

  const ownerOptions = useMemo(() => {
    const set = new Set(
      (items || [])
        .map((it) => String(it.owner || "").trim())
        .filter(Boolean)
    );
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ym = new Date().toISOString().slice(0, 7);
    return (items || []).filter((it) => {
      if (filterStatus !== "all" && String(it.status || "") !== filterStatus) return false;
      if (filterOwner === "__unassigned__") {
        if (String(it.owner || "").trim()) return false;
      } else if (filterOwner !== "all" && String(it.owner || "") !== filterOwner) {
        return false;
      }
      if (overdueOnly) {
        const rd = String(it.due_date || "").trim();
        if (!rd || rd >= today) return false;
      }
      if (dueThisMonthOnly) {
        const rd = String(it.due_date || "").trim();
        if (!rd.startsWith(ym)) return false;
      }
      return true;
    });
  }, [items, filterStatus, filterOwner, overdueOnly, dueThisMonthOnly]);

  const selectableFilteredIds = useMemo(
    () => filteredItems.filter((it) => canRequestApprovalForItem(it)).map((it) => String(it.id)),
    [filteredItems, canRequestApprovalForItem]
  );
  const selectedSet = useMemo(() => new Set(selectedIds.map((x) => String(x))), [selectedIds]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => selectedSet.has(String(id)));
  const selectedEligibleItems = useMemo(
    () => filteredItems.filter((it) => selectedSet.has(String(it.id)) && canRequestApprovalForItem(it)),
    [filteredItems, selectedSet, canRequestApprovalForItem]
  );

  useEffect(() => {
    const liveIds = new Set((items || []).map((it) => String(it.id)));
    setSelectedIds((prev) => prev.filter((id) => liveIds.has(String(id))));
  }, [items]);

  const applyPreset = (preset) => {
    if (preset === "open_only") {
      setFilterStatus("open");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "action_in_progress") {
      setFilterStatus("action_in_progress");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterStatus("all");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    setFilterStatus("all");
    setFilterOwner("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const toggleSelectOne = (itemId, checked) => {
    const id = String(itemId);
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      for (const id of selectableFilteredIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return Array.from(next);
    });
  };

  const handleBulkRequestApproval = async () => {
    if (!selectedEligibleItems.length) {
      toast.error("Select at least one eligible row");
      return;
    }
    const notes = window.prompt("Optional bulk approval request note:", "") || "";
    try {
      setBulkRequesting(true);
      const { ok, failed } = await requestBulkEmsApproval(
        requestEmsApprovalApi,
        MODULE_KEY,
        selectedEligibleItems,
        notes
      );
      if (ok > 0) toast.success(`Requested approval for ${ok} item(s)`);
      if (failed > 0) toast.error(`${failed} item(s) failed`);
      setSelectedIds([]);
      await loadItems();
    } finally {
      setBulkRequesting(false);
    }
  };

  return (
    <div className="dashboard-page guided-register-page">
      <div className="dashboard-header">
        <div>
          <p className="guided-register-eyebrow">ISO 14001 improvement</p>
          <h1>Non-Conformance & Corrective Actions</h1>
          <p className="subtitle">Record issues, contain them, identify the true cause, and track corrective action evidence through to verified closure.</p>
        </div>
      </div>

      <section className="card guided-register-guidance">
        <div>
          <p className="guided-register-eyebrow">How to manage an issue</p>
          <h3>Describe the problem, identify its cause, then prove the fix works</h3>
          <p>Use one entry for each non-conformance. Do not close it until the cause, corrective action, owner, dates and evidence show the issue has been dealt with effectively.</p>
        </div>
        <div className="guided-register-guidance-grid">
          <div><strong>Contain and describe</strong><span>Record what happened, where it came from and any immediate action taken to control the issue.</span></div>
          <div><strong>Find the cause</strong><span>Identify why the issue occurred, rather than only repeating the visible symptom.</span></div>
          <div><strong>Correct and verify</strong><span>Assign an action and owner, set a due date, then attach evidence before closure.</span></div>
        </div>
      </section>
      <div className="card settings-card guided-register-form">
        <div className="settings-header"><div><p className="guided-register-eyebrow">Your corrective-action record</p><h3>{editingId ? "Edit Entry" : "Add Entry"}</h3></div><span className="kpi-sub">Keep the cause, corrective action and closure evidence together for a clear audit trail.</span></div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} labels={isoClauseLabels} />
            <label className="settings-field">
              <span>Title</span>
              <input placeholder="e.g. Waste containers not labelled consistently" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving} />
              <small>Give the issue a short name that makes it easy to identify later.</small>
            </label>
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving}>
                <option value="open">open</option>
                <option value="investigating">investigating</option>
                <option value="action_in_progress">action_in_progress</option>
                <option value="closed">closed</option>
              </select>
              <small>Use open or investigating first; close only after the corrective action and evidence are complete.</small>
            </label>
            <label className="settings-field">
              <span>Source</span>
              <input placeholder="e.g. Internal audit, incident report or customer complaint" value={form.source} onChange={(e) => setField("source", e.target.value)} disabled={saving} />
              <small>State where the issue was identified or reported.</small>
            </label>
            <label className="settings-field">
              <span>Owner</span>
              <input placeholder="e.g. Warehouse Manager" value={form.owner} onChange={(e) => setField("owner", e.target.value)} disabled={saving} />
              <small>Name the person or role accountable for delivering the action.</small>
            </label>
            <label className="settings-field">
              <span>Due date</span>
              <input type="date" value={form.due_date} onChange={(e) => setField("due_date", e.target.value)} disabled={saving} />
              <small>Set a realistic deadline for completing the corrective action.</small>
            </label>
            <label className="settings-field">
              <span>Closure date</span>
              <input type="date" value={form.closure_date} onChange={(e) => setField("closure_date", e.target.value)} disabled={saving} />
              <small>Enter this only after the solution has been checked and the entry can be closed.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Description</span>
              <input placeholder="Describe what happened, where, when, and the actual or potential environmental consequence." value={form.description} onChange={(e) => setField("description", e.target.value)} disabled={saving} />
              <small>Record enough factual detail for someone who was not present to understand the issue.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Root cause</span>
              <input placeholder="e.g. No documented labelling check during goods-in process" value={form.root_cause} onChange={(e) => setField("root_cause", e.target.value)} disabled={saving} />
              <small>State why the issue happened, not just what was observed.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Corrective action</span>
              <input placeholder="e.g. Add a labelled-bin check to the weekly warehouse inspection" value={form.corrective_action} onChange={(e) => setField("corrective_action", e.target.value)} disabled={saving} />
              <small>Describe the permanent change that will prevent recurrence and how it will be checked.</small>
            </label>
            <EvidenceReferenceField
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving}
              helpText="Link photographs, investigation notes, revised procedures, inspection records or evidence that the action is effective."
            />
            <label className="settings-field guided-register-wide-field">
              <span>Notes</span>
              <input placeholder="Optional: record containment, checks completed, management review or closure decisions." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving} />
              <small>Use this space for supporting context without replacing the formal cause and action fields.</small>
            </label>
          </div>
          {submitAttempted && validationErrors.length > 0 ? (
            <div className="form-error-summary">
              Please fix the following before saving:
              <ul>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="settings-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving} style={{ marginRight: 10 }}>
                Cancel
              </button>
            ) : null}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update entry" : "Add entry"}
            </button>
          </div>
        </form>
      </div>

      <BulkActionBar
        selectedCount={selectedIds.length}
        eligibleCount={selectedEligibleItems.length}
        onClear={() => setSelectedIds([])}
        onRequest={handleBulkRequestApproval}
        requesting={bulkRequesting}
        requestDisabled={bulkRequesting || selectedEligibleItems.length === 0}
        onExport={handleExportCsv}
        exporting={exporting}
      />

      <div className="filter-bar" style={{ gridTemplateColumns: "180px 180px auto 1fr" }}>
        <label className="settings-field">
          <span>Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">all</option>
            <option value="open">open</option>
            <option value="investigating">investigating</option>
            <option value="action_in_progress">action_in_progress</option>
            <option value="closed">closed</option>
          </select>
        </label>

        <label className="settings-field">
          <span>Owner</span>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue due-date only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("open_only")}>Only open</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("action_in_progress")}>Action in progress</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                  disabled={!selectableFilteredIds.length}
                />
              </th>
              <th>ISO Clause</th>
              <th>Title</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Owner</th>
              <th>Due date</th>
              <th>Closure date</th>
              <th>Updated by</th>
              <th>Updated at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={11} className="table-empty">No non-conformance entries yet.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(it.id))}
                      onChange={(e) => toggleSelectOne(it.id, e.target.checked)}
                      disabled={!canRequestApprovalForItem(it)}
                    />
                  </td>
                  <td>{it.iso_clause ? (isoClauseLabels[it.iso_clause] ? `${it.iso_clause} - ${isoClauseLabels[it.iso_clause]}` : `Clause ${it.iso_clause}`) : "-"}</td>
                  <td>{it.title || "-"}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status || "-"}</span></td>
                  <td><span className={approvalBadgeClass(it.approval_status)}>{it.approval_status || "not_requested"}</span></td>
                  <td>{it.owner || "-"}</td>
                  <td>{it.due_date || "-"}</td>
                  <td>{it.closure_date || "-"}</td>
                  <td>{it.updated_by || "-"}</td>
                  <td>{it.updated_at || "-"}</td>
                  <td>
                    <button className="admin-btn secondary" onClick={() => startEdit(it)} style={{ marginRight: 8 }}>Edit</button>
                    {canRequestApprovalForItem(it) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleRequestApproval(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Request"}
                      </button>
                    ) : null}
                    {workflowMode === "two_step" && canReview && isApprovalPendingReview(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "review")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Review"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {canApprove && isApprovalPending(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "approve")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Approve"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {closureVerificationRequired
                    && canVerify
                    && isCompletionStatus(it.status)
                    && isApprovalApproved(it)
                    && !Boolean(it.closure_verified) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleVerifyClose(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Verify close"}
                      </button>
                    ) : null}
                    <button className="admin-btn danger" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id}>
                      {deletingId === it.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



